import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";
import { toast } from "react-toastify";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import PublishIcon from "@material-ui/icons/Publish";
import {
  Grid,
  TablePagination,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import ManualAddContactsModal from "../../components/ManualAddContactsModal";
import ContactListProImportWizard from "../../components/ContactListProImportWizard";
import { isArray } from "lodash";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_DATA") {
    const data = action.payload;
    const newData = [];
    if (isArray(data)) {
      data.forEach((item) => {
        const index = state.findIndex((u) => u.id === item.id);
        if (index !== -1) {
          state[index] = item;
        } else {
          newData.push(item);
        }
      });
    }
    return [...state, ...newData];
  }
  if (action.type === "DELETE_DATA") {
    const id = action.payload;
    const index = state.findIndex((u) => u.id === id);
    if (index !== -1) {
      state.splice(index, 1);
    }
    return [...state];
  }
  if (action.type === "UPDATE_DATA") {
    const item = action.payload;
    const index = state.findIndex((u) => u.id === item.id);
    if (index === -1) return state;
    return state.map((u, i) => (i === index ? { ...u, ...item } : u));
  }
  if (action.type === "RESET") {
    return [];
  }
  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  headerCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
  },
  tableHeader: {
    fontWeight: "bold",
    backgroundColor: theme.palette.grey[100],
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  variableChip: {
    margin: theme.spacing(0.25),
    fontSize: "0.7rem",
    height: 20,
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
}));

const ContactListProItems = () => {
  const classes = useStyles();
  const history = useHistory();
  const { contactListProId } = useParams();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [contactList, setContactList] = useState(null);

  const [contacts, dispatch] = useReducer(reducer, []);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [manualAddOpen, setManualAddOpen] = useState(false);
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", number: "", var1: "", var2: "", var3: "", var4: "", var5: "", var6: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Carregar dados da lista
  useEffect(() => {
    const fetchContactList = async () => {
      try {
        const { data } = await api.get(`/contact-lists-pro/${contactListProId}`);
        setContactList(data);
      } catch (err) {
        toastError(err);
        history.push("/campaigns-pro");
      }
    };
    fetchContactList();
  }, [contactListProId, history]);

  // Reset ao mudar filtros
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setSelectedContactIds([]);
  }, [searchParam, pageSize, filterType]);

  const fetchContacts = useCallback(async () => {
    try {
      const { data } = await api.get(`/contact-lists-pro/${contactListProId}/items`, {
        params: {
          searchParam,
          pageNumber,
          pageSize,
          filterType,
        },
      });

      if (pageNumber === 1) {
        dispatch({ type: "RESET" });
      }
      dispatch({ type: "LOAD_DATA", payload: data.records });
      setTotalCount(data.count || 0);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  }, [contactListProId, searchParam, pageNumber, pageSize, filterType]);

  // Carregar contatos
  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [fetchContacts]);

  // Socket listeners
  useEffect(() => {
    const companyId = user.companyId;

    const onContactListItemPro = (data) => {
      if (data.action === "delete") {
        dispatch({ type: "DELETE_DATA", payload: +data.id });
      }
      if (data.action === "update" && data.record) {
        dispatch({ type: "UPDATE_DATA", payload: data.record });
      }
    };

    socket.on(`company-${companyId}-ContactListItemPro`, onContactListItemPro);

    return () => {
      socket.off(`company-${companyId}-ContactListItemPro`, onContactListItemPro);
    };
  }, [user, socket]);

  const refetchListAndContacts = async () => {
    try {
      const [listRes, itemsRes] = await Promise.all([
        api.get(`/contact-lists-pro/${contactListProId}`),
        api.get(`/contact-lists-pro/${contactListProId}/items`, {
          params: { searchParam, pageNumber, pageSize, filterType },
        }),
      ]);
      setContactList(listRes.data);
      if (pageNumber === 1) dispatch({ type: "RESET" });
      dispatch({ type: "LOAD_DATA", payload: itemsRes.data.records });
      setTotalCount(itemsRes.data.count || 0);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSearch = (event) => {
    if (event && event.target != null) setSearchParam(String(event.target.value || "").toLowerCase());
  };

  const handlePageChange = (event, newPage) => {
    setPageNumber(newPage + 1);
  };

  const handlePageSizeChange = (event) => {
    const raw = event && event.target && event.target.value;
    if (raw != null) {
      setPageSize(parseInt(String(raw), 10));
      setPageNumber(1);
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contact-lists-pro/${contactListProId}/items/${contactId}`);
      toast.success("Contato excluído!");
      dispatch({ type: "DELETE_DATA", payload: contactId });
      setSelectedContactIds((prev) => prev.filter((id) => id !== contactId));
      setTotalCount((prev) => prev - 1);
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
  };

  const allVisibleSelected =
    contacts.length > 0 && contacts.every((contact) => selectedContactIds.includes(contact.id));
  const someVisibleSelected =
    contacts.some((contact) => selectedContactIds.includes(contact.id)) && !allVisibleSelected;

  const handleToggleSelectAllVisible = (checked) => {
    if (checked) {
      const visibleIds = contacts.map((contact) => contact.id);
      setSelectedContactIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
      return;
    }
    const visibleSet = new Set(contacts.map((contact) => contact.id));
    setSelectedContactIds((prev) => prev.filter((id) => !visibleSet.has(id)));
  };

  const handleToggleSelectContact = (contactId, checked) => {
    setSelectedContactIds((prev) => {
      if (checked) return Array.from(new Set([...prev, contactId]));
      return prev.filter((id) => id !== contactId);
    });
  };

  const handleBulkDeleteSelected = async () => {
    if (selectedContactIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(
        selectedContactIds.map((contactId) =>
          api.delete(`/contact-lists-pro/${contactListProId}/items/${contactId}`)
        )
      );
      toast.success(`${selectedContactIds.length} contato(s) excluído(s)!`);
      setSelectedContactIds([]);
      await refetchListAndContacts();
    } catch (err) {
      toastError(err);
    } finally {
      setBulkDeleting(false);
    }
  };

  const openEditModal = (contact) => {
    if (!contact || typeof contact !== "object") return;
    setEditingContact(contact);
    setEditForm({
      name: contact.name != null ? String(contact.name) : "",
      number: contact.number != null ? String(contact.number) : "",
      var1: contact.var1 != null ? String(contact.var1) : "",
      var2: contact.var2 != null ? String(contact.var2) : "",
      var3: contact.var3 != null ? String(contact.var3) : "",
      var4: contact.var4 != null ? String(contact.var4) : "",
      var5: contact.var5 != null ? String(contact.var5) : "",
      var6: contact.var6 != null ? String(contact.var6) : "",
    });
  };

  const closeEditModal = () => {
    setEditingContact(null);
    setSavingEdit(false);
  };

  const handleSaveEdit = async () => {
    if (!editingContact) return;
    if (!editForm.number || !editForm.number.trim()) {
      toast.error("Número é obrigatório.");
      return;
    }
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/contact-lists-pro/${contactListProId}/items/${editingContact.id}`, {
        name: editForm.name.trim() || null,
        number: editForm.number.trim(),
        var1: editForm.var1.trim() || null,
        var2: editForm.var2.trim() || null,
        var3: editForm.var3.trim() || null,
        var4: editForm.var4.trim() || null,
        var5: editForm.var5.trim() || null,
        var6: editForm.var6.trim() || null,
      });
      dispatch({ type: "UPDATE_DATA", payload: data });
      toast.success("Contato atualizado!");
      closeEditModal();
    } catch (err) {
      toastError(err);
    } finally {
      setSavingEdit(false);
    }
  };

  const renderVariables = (contact) => {
    if (!contact) return <Typography variant="caption" color="textSecondary">—</Typography>;
    const vars = [];
    const v1 = contact.var1 != null ? String(contact.var1) : "";
    const v2 = contact.var2 != null ? String(contact.var2) : "";
    const v3 = contact.var3 != null ? String(contact.var3) : "";
    const v4 = contact.var4 != null ? String(contact.var4) : "";
    const v5 = contact.var5 != null ? String(contact.var5) : "";
    const v6 = contact.var6 != null ? String(contact.var6) : "";
    if (v1) vars.push({ key: "var1", value: v1 });
    if (v2) vars.push({ key: "var2", value: v2 });
    if (v3) vars.push({ key: "var3", value: v3 });
    if (v4) vars.push({ key: "var4", value: v4 });
    if (v5) vars.push({ key: "var5", value: v5 });
    if (v6) vars.push({ key: "var6", value: v6 });

    if (vars.length === 0) return <Typography variant="caption" color="textSecondary">—</Typography>;

    return (
      <Box>
        {vars.map((v) => (
          <Tooltip key={v.key} title={`${v.key}: ${v.value}`}>
            <Chip
              label={`${v.key}: ${v.value.substring(0, 15)}${v.value.length > 15 ? "..." : ""}`}
              size="small"
              className={classes.variableChip}
              variant="outlined"
            />
          </Tooltip>
        ))}
      </Box>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingContact && `Excluir contato "${deletingContact.name || deletingContact.number}"?`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteContact(deletingContact.id)}
      >
        Esta ação não pode ser desfeita.
      </ConfirmationModal>

      <MainHeader>
        <Box display="flex" alignItems="center">
          <IconButton
            className={classes.backButton}
            onClick={() => history.push("/campaigns-pro")}
          >
            <ArrowBackIcon />
          </IconButton>
          <Title>
            📋 Contatos: {contactList?.name || "Carregando..."}
          </Title>
        </Box>
      </MainHeader>

      <Paper className={classes.headerCard}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              placeholder="Buscar por nome ou número..."
              type="search"
              value={searchParam}
              onChange={handleSearch}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" justifyContent="flex-end">
              <FormControl variant="outlined" size="small" style={{ minWidth: 170 }}>
                <InputLabel>Filtro</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(String(e.target.value || "all"))}
                  label="Filtro"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="valid">Válidos</MenuItem>
                  <MenuItem value="invalid">Inválidos</MenuItem>
                  <MenuItem value="duplicate">Duplicados</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={() => setManualAddOpen(true)}
              >
                Adicionar manualmente
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<PublishIcon />}
                onClick={() => setImportWizardOpen(true)}
              >
                Importar de arquivo
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<DeleteOutlineIcon />}
                onClick={handleBulkDeleteSelected}
                disabled={bulkDeleting || selectedContactIds.length === 0}
              >
                {bulkDeleting ? "Excluindo..." : `Excluir selecionados (${selectedContactIds.length})`}
              </Button>
              <Chip
                label={`Total: ${totalCount} contatos`}
                color="primary"
                clickable
                variant={filterType === "all" ? "default" : "outlined"}
                onClick={() => setFilterType("all")}
              />
              {contactList && (
                <>
                  <Chip
                    label={`Importados: ${contactList.totalImported || 0}`}
                    style={{ backgroundColor: "#4caf50", color: "white" }}
                    clickable
                    onClick={() => setFilterType("valid")}
                    variant={filterType === "valid" ? "default" : "outlined"}
                  />
                  <Chip
                    label={`Duplicados: ${contactList.totalDuplicates || 0}`}
                    style={{ backgroundColor: "#ff9800", color: "white" }}
                    clickable
                    onClick={() => setFilterType("duplicate")}
                    variant={filterType === "duplicate" ? "default" : "outlined"}
                  />
                  <Chip
                    label={`Inválidos: ${contactList.totalInvalid || 0}`}
                    style={{ backgroundColor: "#f44336", color: "white" }}
                    clickable
                    onClick={() => setFilterType("invalid")}
                    variant={filterType === "invalid" ? "default" : "outlined"}
                  />
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <ManualAddContactsModal
        open={manualAddOpen}
        onClose={() => setManualAddOpen(false)}
        contactListProId={contactListProId ? Number(contactListProId) : undefined}
        onSuccess={refetchListAndContacts}
      />
      <ContactListProImportWizard
        open={importWizardOpen}
        onClose={() => setImportWizardOpen(false)}
        onSuccess={refetchListAndContacts}
        existingListId={contactListProId ? Number(contactListProId) : null}
        existingListName={contactList?.name || ""}
      />

      <Dialog open={!!editingContact} onClose={closeEditModal} maxWidth="sm" fullWidth>
        <DialogTitle>Editar contato</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                value={editForm.name ?? ""}
                onChange={(e) => {
                  const value = e.target != null ? e.target.value : "";
                  setEditForm((prev) => ({ ...prev, name: value }));
                }}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Número *"
                value={editForm.number ?? ""}
                onChange={(e) => {
                  const value = e.target != null ? e.target.value : "";
                  setEditForm((prev) => ({ ...prev, number: value }));
                }}
                variant="outlined"
                size="small"
                placeholder="5511999999999"
              />
            </Grid>
            {["var1", "var2", "var3", "var4", "var5", "var6"].map((key, i) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  fullWidth
                  label={`Variável ${i + 1}`}
                  value={editForm[key] ?? ""}
                  onChange={(e) => {
                    const value = e.target != null ? e.target.value : "";
                    setEditForm((prev) => ({ ...prev, [key]: value }));
                  }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditModal}>Cancelar</Button>
          <Button onClick={handleSaveEdit} color="primary" variant="contained" disabled={savingEdit}>
            {savingEdit ? "Salvando…" : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader} padding="checkbox">
                <Checkbox
                  color="primary"
                  indeterminate={someVisibleSelected}
                  checked={allVisibleSelected}
                  onChange={(e) => handleToggleSelectAllVisible(Boolean(e.target.checked))}
                />
              </TableCell>
              <TableCell className={classes.tableHeader}>Nome</TableCell>
              <TableCell className={classes.tableHeader}>Número</TableCell>
              <TableCell className={classes.tableHeader}>Variáveis</TableCell>
              <TableCell className={classes.tableHeader}>Tags</TableCell>
              <TableCell className={classes.tableHeader} align="center">Opt-in</TableCell>
              <TableCell className={classes.tableHeader} align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    checked={selectedContactIds.includes(contact.id)}
                    onChange={(e) => handleToggleSelectContact(contact.id, Boolean(e.target.checked))}
                  />
                </TableCell>
                <TableCell>{contact.name || <em style={{ color: "#999" }}>Sem nome</em>}</TableCell>
                <TableCell>
                  <Typography variant="body2" style={{ fontFamily: "monospace" }}>
                    {contact.number}
                  </Typography>
                </TableCell>
                <TableCell>{renderVariables(contact)}</TableCell>
                <TableCell>
                  {contact.tags ? (
                    <Chip label={contact.tags} size="small" />
                  ) : (
                    <Typography variant="caption" color="textSecondary">—</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={contact.optedIn ? "Sim" : "Não"}
                    size="small"
                    color={contact.optedIn ? "primary" : "default"}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar">
                    <IconButton
                      size="small"
                      onClick={() => openEditModal(contact)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingContact(contact);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={7} />}
            {!loading && contacts.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className={classes.emptyState}>
                  <Typography variant="body1">
                    Nenhum contato encontrado.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <Box display="flex" justifyContent="flex-end" p={2}>
          <TablePagination
            component="div"
            count={totalCount}
            page={pageNumber - 1}
            onPageChange={handlePageChange}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handlePageSizeChange}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage="Por página:"
          />
        </Box>
      </Paper>
    </MainContainer>
  );
};

export default ContactListProItems;

